import { z } from "zod";
import { colorSchema, timestampSchema, uuidSchema } from "./api.ts";
import { blankToNull, entityTypeSchema, isHttpUrl, isUnique, queryBooleanSchema, userRefSchema, type EntityType } from "./common.ts";
import { CURRENCIES, currencyCodeSchema, type Money } from "./money.ts";

/**
 * Custom fields — /api/v1/custom-fields — and saved views — /api/v1/views.
 * Reads: any member. Custom-field writes: `org:manage`. Views: owner, or
 * `org:manage` for shared views.
 *
 * VALUES live on each record as `customFields: Record<fieldKey, CustomFieldValue>`
 * (keyed by the immutable `key`, not the id). Owning modules validate writes with
 * `validateCustomFieldValues(activeDefs, values, { partial })` and store the
 * normalized `values` (JSONB). Tables/filters/sorts reference them as "cf:<key>".
 */

export const CUSTOM_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "currency",
  "date",
  "select",
  "multiselect",
  "checkbox",
  "url",
  "email",
  "phone",
  "user",
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];
export const customFieldTypeSchema = z.enum(CUSTOM_FIELD_TYPES);

/** Record types that support custom fields. */
export const CUSTOM_FIELD_ENTITY_TYPES = [
  "lead",
  "client",
  "company",
  "contact",
  "project",
  "task",
  "proposal",
  "contract",
  "invoice",
  "expense",
  "service",
  "activity",
] as const satisfies readonly EntityType[];
export type CustomFieldEntityType = (typeof CUSTOM_FIELD_ENTITY_TYPES)[number];
export const customFieldEntityTypeSchema = z.enum(CUSTOM_FIELD_ENTITY_TYPES);

export const CUSTOM_FIELD_REF_PREFIX = "cf:";
export function customFieldRef(key: string): string {
  return `${CUSTOM_FIELD_REF_PREFIX}${key}`;
}
export const MAX_CUSTOM_FIELDS_PER_ENTITY = 100;
export const CUSTOM_FIELD_DEFAULT_MAX_LENGTH: Partial<Record<CustomFieldType, number>> = { text: 500, textarea: 10_000 };

/** snake_case machine key, immutable after creation. */
export const customFieldKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, "Lowercase letters, digits and _; start with a letter; max 40");

/** Pure: "Budget (₹) 2026" → "budget_2026". */
export function customFieldKeyFromLabel(label: string): string {
  let key = label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!key) key = "field";
  if (/^[0-9]/.test(key)) key = `f_${key}`;
  return key.slice(0, 40).replace(/_+$/, "");
}

/** Select option. `id` is stable (values store it), `label` may be renamed. */
export const customFieldOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  color: z.string().nullable(),
});
export type CustomFieldOption = z.infer<typeof customFieldOptionSchema>;
export const customFieldOptionInputSchema = z
  .object({
    /** Client-generated stable id (e.g. nanoid / slug). */
    id: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/, "Invalid option id"),
    label: z.string().trim().min(1).max(80),
    color: colorSchema.nullable().optional(),
  })
  .strict();

/** Type-specific settings. number: min/max/decimals; currency: currency/min/max (minor units); text: maxLength. */
export const customFieldSettingsSchema = z.object({
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
  decimals: z.number().int().min(0).max(6).nullable().optional(),
  maxLength: z.number().int().min(1).max(10_000).nullable().optional(),
  /** currency: fixed currency (null = any enabled org currency). */
  currency: currencyCodeSchema.nullable().optional(),
  placeholder: z.string().max(120).nullable().optional(),
});
export type CustomFieldSettings = z.infer<typeof customFieldSettingsSchema>;
export const customFieldSettingsInputSchema = customFieldSettingsSchema.strict();

export const customFieldDefinitionSchema = z.object({
  id: uuidSchema,
  entityType: customFieldEntityTypeSchema,
  key: z.string(),
  label: z.string(),
  type: customFieldTypeSchema,
  description: z.string().nullable(),
  required: z.boolean(),
  /** Non-empty only for select / multiselect. */
  options: z.array(customFieldOptionSchema),
  settings: customFieldSettingsSchema,
  position: z.number().int(),
  /** Shown (read-only) to clients in the portal. */
  visibleInPortal: z.boolean(),
  archivedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type CustomFieldDefinition = z.infer<typeof customFieldDefinitionSchema>;

/** GET /api/v1/custom-fields?entityType=&includeArchived= → { items: CustomFieldDefinition[] } ordered by entityType, position. */
export const listCustomFieldsQuerySchema = z.object({
  entityType: customFieldEntityTypeSchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
});

const optionsListInput = z
  .array(customFieldOptionInputSchema)
  .max(200)
  .refine((o) => isUnique(o.map((x) => x.id)), "Duplicate option id")
  .refine((o) => isUnique(o.map((x) => x.label.toLowerCase())), "Duplicate option label");

/**
 * POST /api/v1/custom-fields → 201 CustomFieldDefinition. `key` defaults to
 * customFieldKeyFromLabel(label) (suffixed "_2", "_3"… if taken). `position`
 * defaults to last. Duplicate key → CONFLICT.
 */
export const createCustomFieldInputSchema = z
  .object({
    entityType: customFieldEntityTypeSchema,
    key: customFieldKeySchema.optional(),
    label: z.string().trim().min(1).max(80),
    type: customFieldTypeSchema,
    description: blankToNull(z.string().max(300)).optional(),
    required: z.boolean().default(false),
    options: optionsListInput.default(() => []),
    settings: customFieldSettingsInputSchema.default(() => ({})),
    position: z.number().int().min(0).optional(),
    visibleInPortal: z.boolean().default(false),
  })
  .strict()
  .superRefine((o, ctx) => {
    const isSelect = o.type === "select" || o.type === "multiselect";
    if (isSelect && o.options.length === 0) ctx.addIssue({ code: "custom", path: ["options"], message: "Add at least one option" });
    if (!isSelect && o.options.length > 0) ctx.addIssue({ code: "custom", path: ["options"], message: "Options apply only to select fields" });
    const s = o.settings;
    if (s.min != null && s.max != null && s.min > s.max) ctx.addIssue({ code: "custom", path: ["settings", "max"], message: "Max must be ≥ min" });
  });
export type CreateCustomFieldInput = z.input<typeof createCustomFieldInputSchema>;

/**
 * PATCH /api/v1/custom-fields/:id → CustomFieldDefinition. `entityType`, `key`
 * and `type` are immutable. `archived: false` restores an archived field.
 * Removing an option that records still use is allowed (values become invalid
 * and are reported on next write).
 */
export const updateCustomFieldInputSchema = z
  .object({
    label: z.string().trim().min(1).max(80),
    description: blankToNull(z.string().max(300)),
    required: z.boolean(),
    options: optionsListInput,
    settings: customFieldSettingsInputSchema,
    position: z.number().int().min(0),
    visibleInPortal: z.boolean(),
    archived: z.boolean(),
  })
  .partial()
  .strict()
  .refine((o) => Object.keys(o).length > 0, "Nothing to update");
export type UpdateCustomFieldInput = z.input<typeof updateCustomFieldInputSchema>;

/** POST /api/v1/custom-fields/reorder → { items: CustomFieldDefinition[] } (ids in new order; positions rewritten 0..n-1). */
export const reorderCustomFieldsInputSchema = z
  .object({
    entityType: customFieldEntityTypeSchema,
    ids: z.array(uuidSchema).min(1).max(500).refine(isUnique, "Duplicate id"),
  })
  .strict();

/** DELETE /api/v1/custom-fields/:id → 204. Archives (values are kept, hidden, restorable). */

// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------

/**
 * Stored/returned value per type: text|textarea|url|email|phone → string;
 * number → number; currency → Money; date → "YYYY-MM-DD"; select → option id;
 * multiselect → option id[]; checkbox → boolean; user → user id; unset → null.
 */
export type CustomFieldValue = string | number | boolean | string[] | Money | null;
export type CustomFieldValues = Record<string, CustomFieldValue>;

/** DTO shape of `customFields` on records. */
export const customFieldValuesSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.object({ amountMinor: z.number().int(), currency: currencyCodeSchema }), z.null()]),
);
/** INPUT shape of `customFields` on record create/update — contents checked by validateCustomFieldValues. */
export const customFieldValuesInputSchema = z.record(z.string().max(40), z.unknown());

export const CUSTOM_FIELD_ERROR_CODES = [
  "required",
  "unknown_field",
  "invalid_type",
  "too_long",
  "out_of_range",
  "invalid_option",
  "invalid_format",
  "invalid_currency",
] as const;
export type CustomFieldErrorCode = (typeof CUSTOM_FIELD_ERROR_CODES)[number];

export interface CustomFieldValueError {
  key: string;
  code: CustomFieldErrorCode;
  message: string;
}

export interface CustomFieldValidationResult {
  ok: boolean;
  /** Normalized values (trimmed, "" → null, option labels → ids, deduped). */
  values: CustomFieldValues;
  errors: CustomFieldValueError[];
}

/** Minimal definition needed for validation (a full CustomFieldDefinition fits). */
export type CustomFieldDefinitionLike = Pick<CustomFieldDefinition, "key" | "label" | "type" | "required" | "options"> & {
  settings?: CustomFieldSettings | null;
  archivedAt?: string | null;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9 ()-]{6,20}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function isValidDateOnly(s: string): boolean {
  const m = DATE_RE.exec(s);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

function matchOption(def: CustomFieldDefinitionLike, raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim();
  const byId = def.options.find((o) => o.id === v);
  if (byId) return byId.id;
  const byLabel = def.options.find((o) => o.label.toLowerCase() === v.toLowerCase());
  return byLabel ? byLabel.id : null;
}

type Checked = { value: CustomFieldValue } | { error: Omit<CustomFieldValueError, "key"> };

function checkValue(def: CustomFieldDefinitionLike, raw: unknown, allowedCurrencies?: readonly string[]): Checked {
  const s = def.settings ?? {};
  const label = def.label;
  const err = (code: CustomFieldErrorCode, message: string): Checked => ({ error: { code, message } });
  if (raw === null || raw === undefined) return { value: null };

  switch (def.type) {
    case "text":
    case "textarea":
    case "url":
    case "email":
    case "phone": {
      if (typeof raw !== "string" && typeof raw !== "number") return err("invalid_type", `${label} must be text`);
      let v = String(raw).trim();
      if (v === "") return { value: null };
      if (def.type === "text" || def.type === "textarea") {
        const max = s.maxLength ?? CUSTOM_FIELD_DEFAULT_MAX_LENGTH[def.type] ?? 500;
        if (v.length > max) return err("too_long", `${label} must be at most ${max} characters`);
        if (def.type === "text") v = v.replace(/\s+/g, " ");
        return { value: v };
      }
      if (def.type === "url") {
        if (v.length > 2048 || !isHttpUrl(v)) return err("invalid_format", `${label} must be a valid http(s) URL`);
        return { value: v };
      }
      if (def.type === "email") {
        v = v.toLowerCase();
        if (v.length > 254 || !EMAIL_RE.test(v)) return err("invalid_format", `${label} must be a valid email`);
        return { value: v };
      }
      if (!PHONE_RE.test(v)) return err("invalid_format", `${label} must be a valid phone number`);
      return { value: v };
    }
    case "number": {
      if (typeof raw === "string" && raw.trim() === "") return { value: null };
      const n = typeof raw === "string" ? Number(raw.trim()) : raw;
      if (typeof n !== "number" || !Number.isFinite(n)) return err("invalid_type", `${label} must be a number`);
      if (s.min != null && n < s.min) return err("out_of_range", `${label} must be ≥ ${s.min}`);
      if (s.max != null && n > s.max) return err("out_of_range", `${label} must be ≤ ${s.max}`);
      if (s.decimals != null && Number(n.toFixed(s.decimals)) !== n) return err("invalid_format", `${label} allows at most ${s.decimals} decimal places`);
      return { value: n };
    }
    case "currency": {
      if (typeof raw !== "object" || Array.isArray(raw)) return err("invalid_type", `${label} must be an amount with a currency`);
      const { amountMinor, currency } = raw as { amountMinor?: unknown; currency?: unknown };
      if (typeof amountMinor !== "number" || !Number.isSafeInteger(amountMinor)) return err("invalid_type", `${label} must be a whole number of minor units`);
      if (typeof currency !== "string" || !(currency in CURRENCIES)) return err("invalid_currency", `${label} has an unsupported currency`);
      if (s.currency && currency !== s.currency) return err("invalid_currency", `${label} must be in ${s.currency}`);
      if (!s.currency && allowedCurrencies && !allowedCurrencies.includes(currency)) return err("invalid_currency", `${label}: ${currency} is not enabled`);
      if (s.min != null && amountMinor < s.min) return err("out_of_range", `${label} is below the minimum`);
      if (s.max != null && amountMinor > s.max) return err("out_of_range", `${label} is above the maximum`);
      return { value: { amountMinor, currency: currency as Money["currency"] } };
    }
    case "date": {
      if (typeof raw !== "string") return err("invalid_type", `${label} must be a date`);
      const v = raw.trim();
      if (v === "") return { value: null };
      if (!isValidDateOnly(v)) return err("invalid_format", `${label} must be a valid date (YYYY-MM-DD)`);
      return { value: v };
    }
    case "select": {
      if (typeof raw === "string" && raw.trim() === "") return { value: null };
      if (typeof raw !== "string") return err("invalid_type", `${label} must be one option`);
      const id = matchOption(def, raw);
      if (!id) return err("invalid_option", `"${raw}" is not an option of ${label}`);
      return { value: id };
    }
    case "multiselect": {
      const arr = typeof raw === "string" ? [raw] : raw;
      if (!Array.isArray(arr)) return err("invalid_type", `${label} must be a list of options`);
      const ids: string[] = [];
      for (const item of arr) {
        if (typeof item === "string" && item.trim() === "") continue;
        const id = matchOption(def, item);
        if (!id) return err("invalid_option", `"${String(item)}" is not an option of ${label}`);
        if (!ids.includes(id)) ids.push(id);
      }
      return { value: ids.length ? ids : null };
    }
    case "checkbox": {
      if (typeof raw !== "boolean") return err("invalid_type", `${label} must be true or false`);
      return { value: raw };
    }
    case "user": {
      if (typeof raw === "string" && raw.trim() === "") return { value: null };
      if (typeof raw !== "string" || !UUID_RE.test(raw.trim())) return err("invalid_type", `${label} must be a user`);
      return { value: raw.trim().toLowerCase() };
    }
  }
}

/**
 * Pure validator used on both sides.
 * - `defs`: the org's definitions for the entity type (archived ones are skipped:
 *   their keys are silently dropped from input and not required).
 * - `partial: false` (create / full replace): every active key appears in the
 *   output (null when missing) and required fields must be non-null.
 * - `partial: true` (PATCH): only provided keys are validated/returned; a
 *   required field may not be set to null.
 * - `allowedCurrencies`: org enabled currencies for `currency` fields.
 * Unknown keys → "unknown_field". Membership of `user` values is checked by the server.
 */
export function validateCustomFieldValues(
  defs: readonly CustomFieldDefinitionLike[],
  values: Record<string, unknown> | null | undefined,
  opts: { partial?: boolean; allowedCurrencies?: readonly string[] } = {},
): CustomFieldValidationResult {
  const input = values ?? {};
  const active = new Map<string, CustomFieldDefinitionLike>();
  const archived = new Set<string>();
  for (const d of defs) {
    if (d.archivedAt) archived.add(d.key);
    else active.set(d.key, d);
  }

  const out: CustomFieldValues = {};
  const errors: CustomFieldValueError[] = [];

  for (const key of Object.keys(input)) {
    if (!active.has(key) && !archived.has(key)) errors.push({ key, code: "unknown_field", message: `Unknown field "${key}"` });
  }
  for (const [key, def] of active) {
    const provided = Object.prototype.hasOwnProperty.call(input, key);
    if (opts.partial && !provided) continue;
    const res = checkValue(def, provided ? input[key] : null, opts.allowedCurrencies);
    if ("error" in res) {
      errors.push({ key, ...res.error });
      continue;
    }
    if (def.required && res.value === null) {
      errors.push({ key, code: "required", message: `${def.label} is required` });
      continue;
    }
    out[key] = res.value;
  }
  return { ok: errors.length === 0, values: out, errors };
}

// ---------------------------------------------------------------------------
// Saved views — /api/v1/views
// ---------------------------------------------------------------------------

export const VIEW_LAYOUTS = ["table", "board", "list", "calendar", "timeline"] as const;
export type ViewLayout = (typeof VIEW_LAYOUTS)[number];
export const viewLayoutSchema = z.enum(VIEW_LAYOUTS);

export const FILTER_OPERATORS = [
  "eq",
  "neq",
  "in",
  "not_in",
  "contains",
  "not_contains",
  "starts_with",
  "gt",
  "gte",
  "lt",
  "lte",
  "between",
  "is_empty",
  "is_not_empty",
  "within_last_days",
  "within_next_days",
] as const;
export type FilterOperator = (typeof FILTER_OPERATORS)[number];
export const filterOperatorSchema = z.enum(FILTER_OPERATORS);

/** Special filter values resolved at query time. */
export const FILTER_TOKENS = { me: "$me", today: "$today" } as const;

/** Column / filter / sort reference: a record field ("status", "owner.id") or "cf:<key>". */
export const fieldRefSchema = z.string().min(1).max(80).regex(/^[A-Za-z][A-Za-z0-9_.:]*$/, "Invalid field reference");

const filterScalar = z.union([z.string().max(500), z.number(), z.boolean(), z.null()]);
export const filterConditionSchema = z.object({
  field: fieldRefSchema,
  op: filterOperatorSchema,
  /** in/not_in → array; between → [from, to]; is_empty/is_not_empty → omitted; within_* → days. */
  value: z.union([filterScalar, z.array(z.union([z.string().max(200), z.number()])).max(100)]).optional(),
});
export type FilterCondition = z.infer<typeof filterConditionSchema>;

export const viewFiltersSchema = z.object({
  match: z.enum(["all", "any"]),
  conditions: z.array(filterConditionSchema).max(30),
  /** Free-text quick filter. */
  q: z.string().max(200).nullable().optional(),
});
export type ViewFilters = z.infer<typeof viewFiltersSchema>;
export const EMPTY_VIEW_FILTERS: ViewFilters = { match: "all", conditions: [] };

export const viewSortSchema = z.array(z.object({ field: fieldRefSchema, order: z.enum(["asc", "desc"]) })).max(3);
export type ViewSort = z.infer<typeof viewSortSchema>;

/**
 * Saved view DTO. Personal views are visible to their owner; shared views to
 * everyone. `isDefault` = the view opened by default (per user for personal,
 * org-wide for shared); setting it unsets the previous default in that scope.
 */
export const savedViewSchema = z.object({
  id: uuidSchema,
  entityType: entityTypeSchema,
  name: z.string(),
  layout: viewLayoutSchema,
  filters: viewFiltersSchema,
  sort: viewSortSchema,
  columns: z.array(z.string()),
  groupBy: z.string().nullable(),
  isShared: z.boolean(),
  isDefault: z.boolean(),
  owner: userRefSchema.nullable(),
  /** Whether the current user may edit/delete it. */
  canEdit: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type SavedView = z.infer<typeof savedViewSchema>;

/** GET /api/v1/views?entityType= → { items: SavedView[] } (own + shared). */
export const listViewsQuerySchema = z.object({ entityType: entityTypeSchema.optional() });

const viewEditableShape = {
  name: z.string().trim().min(1).max(80),
  layout: viewLayoutSchema,
  filters: viewFiltersSchema,
  sort: viewSortSchema,
  columns: z.array(fieldRefSchema).max(60),
  groupBy: fieldRefSchema.nullable(),
  isShared: z.boolean(),
  isDefault: z.boolean(),
};

/** POST /api/v1/views → 201 SavedView. `isShared: true` requires `org:manage`. */
export const createSavedViewInputSchema = z
  .object({
    entityType: entityTypeSchema,
    name: viewEditableShape.name,
    layout: viewEditableShape.layout.default("table"),
    filters: viewEditableShape.filters.default(() => ({ match: "all" as const, conditions: [] })),
    sort: viewEditableShape.sort.default(() => []),
    columns: viewEditableShape.columns.default(() => []),
    groupBy: viewEditableShape.groupBy.default(null),
    isShared: viewEditableShape.isShared.default(false),
    isDefault: viewEditableShape.isDefault.default(false),
  })
  .strict();
export type CreateSavedViewInput = z.input<typeof createSavedViewInputSchema>;

/** PATCH /api/v1/views/:id → SavedView (filters/sort/columns replaced as a whole). DELETE /api/v1/views/:id → 204. */
export const updateSavedViewInputSchema = z
  .object(viewEditableShape)
  .partial()
  .strict()
  .refine((o) => Object.keys(o).length > 0, "Nothing to update");
export type UpdateSavedViewInput = z.input<typeof updateSavedViewInputSchema>;
